import React, { useEffect, useMemo, useState, createElement } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { FilterBar } from './components/FilterBar';
import { SchoolListings } from './components/SchoolListings';
import { ContentSection } from './components/ContentSection';
import { SeoContentSection } from './components/SeoContentSection';
import { Footer } from './components/Footer';
import { ArticlesPage } from './pages/ArticlesPage';
import { CollegesPage } from './pages/CollegesPage';
import { ArticleDetailPage } from './pages/ArticleDetailPage';
import { ProgramLandingPage } from './pages/ProgramLandingPage';
import { TermsPage } from './pages/TermsPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { CANoticePage } from './pages/CANoticePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { RequestInformationPage } from './pages/RequestInformationPage';
import { BrandedCollegePage } from './pages/BrandedCollegePage';
import { FindDegreesBridgePage } from './pages/FindDegreesBridgePage';
import { useSchoolSearch } from './hooks/useSchoolSearch';
import { useGeoLocation } from './hooks/useGeoLocation';
import { useMetaTags } from './hooks/useMetaTags';
import { useMetaPixel } from './hooks/useMetaPixel';
import { useUrlParams } from './hooks/useUrlParams';
import { areaOfStudyData } from './data/areaOfStudyData';
import { getDefaultProgram, getAreaFromProgram, isValidProgram } from './data/filterOptions';
function FilterAccordion({
  degreeLevel,
  selectedAreaId,
  selectedProgram,
  loading,
  schoolCount,
  onDegreeLevelChange,
  onAreaStudyChange,
  onProgramChange
}: {
  degreeLevel: number;
  selectedAreaId: number;
  selectedProgram: number;
  loading: boolean;
  schoolCount: number;
  onDegreeLevelChange: (v: number) => void;
  onAreaStudyChange: (v: number) => void;
  onProgramChange: (v: number) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  return <div className="w-full bg-white border-b border-gray-200 shadow-sm relative z-30" data-id="element-0">
      {/* Accordion toggle — works on both mobile and desktop */}
      <div className="max-w-7xl mx-auto px-4" data-id="element-1">
        <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-3 md:py-4 hover:bg-gray-50/50 transition-colors rounded-lg px-2" data-id="element-2">
          <div className="flex items-center gap-2.5" data-id="element-3">
            <SlidersHorizontalIcon className="w-4 h-4 text-[#3b82f6]" data-id="element-4" />
            <span className="text-sm font-bold text-gray-900" data-id="element-5">
              Find Your Program
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-6">
              {loading ? '...' : `${schoolCount} result${schoolCount !== 1 ? 's' : ''}`}
            </span>
          </div>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-7" />
        </button>
      </div>

      {/* Collapsible content */}
      <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-8">
        <div className="px-4 pb-4 pt-1 max-w-7xl mx-auto" data-id="element-9">
          <p className="text-xs text-gray-500 mb-3" data-id="element-10">
            Select your degree level, area of interest, and program to see
            matching schools.
          </p>

          <FilterBar degreeLevel={degreeLevel} areaStudy={selectedAreaId} program={selectedProgram} onDegreeLevelChange={onDegreeLevelChange} onAreaStudyChange={onAreaStudyChange} onProgramChange={onProgramChange} data-id="element-11" />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-gray-100" data-id="element-12">
            <span className="text-xs text-gray-500 flex items-center gap-1.5" data-id="element-13">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-14"></span>
              All programs are accredited
            </span>
            <span className="text-xs text-gray-400" data-id="element-15">•</span>
            <span className="text-xs text-gray-500" data-id="element-16">100% online options</span>
            <span className="text-xs text-gray-400 hidden sm:inline" data-id="element-17">•</span>
            <span className="text-xs text-gray-500 hidden sm:inline" data-id="element-18">
              Financial aid eligible
            </span>
          </div>
        </div>
      </div>
    </div>;
}
export function App() {
  // Set favicon
  useEffect(() => {
    const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    favicon.type = 'image/svg+xml';
    favicon.rel = 'icon';
    favicon.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='0.9em' font-size='90'>🎓</text></svg>";
    if (!document.querySelector("link[rel*='icon']")) {
      document.head.appendChild(favicon);
    }
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useUrlParams();
  // Derive initial state from URL params or use defaults
  const initialProgram = urlParams.mProgram && isValidProgram(urlParams.mProgram) ? urlParams.mProgram : 900;
  const initialArea = urlParams.mProgram ? getAreaFromProgram(urlParams.mProgram) : 9;
  const initialDegree = urlParams.degreeInterest ?? 5;
  // Derive currentPage from URL path
  const getPageFromPath = (pathname: string) => {
    const path = pathname.replace(/^\/+|\/+$/g, '');
    // Handle dynamic routes
    if (path.startsWith('university/')) {
      return 'university';
    }
    const pathMap: Record<string, string> = {
      'request-information': 'request-information',
      articles: 'articles',
      colleges: 'colleges',
      terms: 'terms',
      'privacy-policy': 'privacy-policy',
      'cookie-policy': 'cookie-policy',
      'ca-notice': 'ca-notice',
      'program-landing': 'program-landing'
    };
    return pathMap[path] || 'compare';
  };
  const [currentPage, setCurrentPage] = useState<string>(getPageFromPath(location.pathname));
  const [currentArticle, setCurrentArticle] = useState<string | null>(null);
  const [degreeLevel, setDegreeLevel] = useState<number>(initialDegree);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(initialArea);
  const [selectedProgram, setSelectedProgram] = useState<number>(initialProgram);
  // Sync currentPage when location changes (e.g. Magic Patterns route dropdown)
  useEffect(() => {
    setCurrentPage(getPageFromPath(location.pathname));
  }, [location.pathname]);
  // Build tracking params from URL (falls back to defaults inside the hook)
  const tracking = useMemo(() => ({
    sub1: urlParams.sub1 ?? undefined,
    sub2: urlParams.sub2 ?? undefined,
    sub3: urlParams.sub3 ?? undefined
  }), [urlParams]);
  const {
    geo
  } = useGeoLocation();
  useMetaPixel();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: selectedProgram,
    zipCode: geo.zipCode,
    tracking
  });
  const currentAreaData = areaOfStudyData[selectedAreaId];
  const metaConfig = useMemo(() => {
    const configs: Record<string, {
      title: string;
      description: string;
      keywords: string;
    }> = {
      compare: {
        title: `Compare Online ${currentAreaData.name} Programs | DegreeCompare`,
        description: `Compare top accredited online ${currentAreaData.name.toLowerCase()} programs. Find the best online colleges, read reviews, and explore degree options for ${new Date().getFullYear()}.`,
        keywords: `online ${currentAreaData.name.toLowerCase()} degree, accredited online programs, compare online colleges, ${currentAreaData.name.toLowerCase()} programs, online education`
      },
      colleges: {
        title: 'Explore Accredited Online Colleges | DegreeCompare',
        description: 'Browse top-rated accredited online colleges and universities. Compare programs, read reviews, and find the right school for your online degree.',
        keywords: 'online colleges, accredited universities, online degree programs, best online schools, college directory'
      },
      articles: {
        title: 'Articles & Guides for Online Students | DegreeCompare',
        description: 'Expert advice on choosing the right online program, financial aid, career planning, and more. Resources for prospective and current online students.',
        keywords: 'online education articles, college guides, financial aid tips, career planning, online degree advice'
      }
    };
    return configs[currentPage] || configs.compare;
  }, [currentPage, currentAreaData]);
  useMetaTags({
    title: metaConfig.title,
    description: metaConfig.description,
    keywords: metaConfig.keywords,
    ogTitle: metaConfig.title,
    ogDescription: metaConfig.description
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  const handleProgramChange = (programId: number) => {
    // Derive the area from the program
    const newAreaId = getAreaFromProgram(programId);
    // If the area changes, update both area and program
    if (newAreaId !== selectedAreaId) {
      setSelectedAreaId(newAreaId);
    }
    setSelectedProgram(programId);
  };
  const handleNavigate = (page: string, articleSlug?: string) => {
    setCurrentPage(page);
    if (articleSlug) {
      setCurrentArticle(articleSlug);
    } else {
      setCurrentArticle(null);
    }
    // Update URL to match the current page
    let urlPath = '/';
    if (page === 'compare') {
      urlPath = '/';
    } else if (page.startsWith('university/')) {
      urlPath = `/${page}`;
    } else {
      urlPath = `/${page}`;
    }
    navigate(urlPath);
    window.scrollTo(0, 0);
  };
  const handleNavigateToProgram = (areaId: number, programId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(programId);
    // Stay on compare page to show filtered results
    window.scrollTo(0, 0);
  };
  const handleNavigateToDegree = (degree: number) => {
    setDegreeLevel(degree);
    setSelectedAreaId(9);
    setSelectedProgram(900);
    setCurrentPage('compare');
    navigate('/');
    window.scrollTo(0, 0);
  };
  // Shared layout wrapper
  const isUniversityPage = location.pathname.startsWith('/university/');
  const isBridgePage = location.pathname.startsWith('/find-degrees/');
  const PageLayout = ({
    children
  }: {
    children: React.ReactNode;
  }) => {
    if (isUniversityPage || isBridgePage) {
      return <div className="min-h-screen bg-gray-50 font-sans text-gray-900 flex flex-col" data-id="element-19">
          <main className="flex-grow" data-id="element-20">{children}</main>
        </div>;
    }
    return <div className="min-h-screen bg-gray-100 font-sans text-gray-900 flex flex-col" data-id="element-21">
        <Header currentPage={currentPage} onNavigate={handleNavigate} onNavigateToProgram={handleNavigateToProgram} onNavigateToDegree={handleNavigateToDegree} data-id="element-22" />
        <main className="flex-grow" data-id="element-23">{children}</main>
        <Footer onNavigate={handleNavigate} data-id="element-24" />
      </div>;
  };
  const ComparePage = () => <>
      <HeroSection areaData={currentAreaData} selectedProgram={selectedProgram} degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} t1Override={urlParams.t1} data-id="element-25" />
      <FilterAccordion degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} loading={loading} schoolCount={schools.length} onDegreeLevelChange={setDegreeLevel} onAreaStudyChange={handleAreaChange} onProgramChange={handleProgramChange} data-id="element-26" />
      <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} onProgramChange={handleProgramChange} data-id="element-27" />
      <SeoContentSection areaStudyId={selectedAreaId} topSchool={schools[0]} data-id="element-28" />
      <ContentSection areaData={currentAreaData} selectedAreaId={selectedAreaId} onNavigate={handleNavigate} data-id="element-29" />
    </>;
  return <PageLayout data-id="element-30">
      <Routes data-id="element-31">
        <Route path="/" element={<ComparePage data-id="element-33" />} data-id="element-32" />
        <Route path="/request-information" element={<RequestInformationPage data-id="element-35" />} data-id="element-34" />
        <Route path="/colleges" element={<CollegesPage data-id="element-37" />} data-id="element-36" />
        <Route path="/university/:collegeSlug" element={<BrandedCollegePage data-id="element-39" />} data-id="element-38" />
        <Route path="/articles" element={currentArticle === 'online-business-degree-worth-it-2026' ? <ArticleDetailPage onNavigate={handleNavigate} onProgramChange={handleProgramChange} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} data-id="element-41" /> : <ArticlesPage onNavigate={handleNavigate} data-id="element-42" />} data-id="element-40" />
        <Route path="/program-landing" element={<ProgramLandingPage programId={selectedProgram} degreeLevel={degreeLevel} onProgramChange={handleProgramChange} data-id="element-44" />} data-id="element-43" />
        <Route path="/terms" element={<TermsPage data-id="element-46" />} data-id="element-45" />
        <Route path="/cookie-policy" element={<CookiePolicyPage data-id="element-48" />} data-id="element-47" />
        <Route path="/ca-notice" element={<CANoticePage data-id="element-50" />} data-id="element-49" />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-52" />} data-id="element-51" />
        <Route path="/find-degrees/:templateId" element={<FindDegreesBridgePage data-id="element-54" />} data-id="element-53" />
      </Routes>
    </PageLayout>;
}